import { Checkbox, ListItemText, MenuItem, Select } from '@mui/material';
import { Key } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Label } from 'wisestep-base';

interface WsCheckmarkSelectProps<T> {
  name: string;
  options: T[];
  getLabel: (option: T) => string;
  getValue?: (option: T) => string | number;
  placeholder?: string;
  label?: string;
  showMandatorySymbol?: boolean;
}

const WsCheckmarkSelect = <T extends object>({
  name,
  options,
  getLabel,
  getValue,
  placeholder = 'Select some options',
  label,
  showMandatorySymbol,
}: WsCheckmarkSelectProps<T>) => {
  const { control } = useFormContext();

  return (
    <>
      {label && (
        <Label>
          {label}
          {showMandatorySymbol ? ' *' : ''}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        render={({ field: { onChange, value } }) => {
          const handleChange = (event: any) => {
            onChange(event.target.value ?? []);
          };

          return (
            <Select
              multiple
              value={value ?? []}
              onChange={handleChange}
              renderValue={(selectedValues: unknown) => {
                const selected = selectedValues as (string | number | T)[];
                if (!selected?.length) return placeholder;
                if (getValue) {
                  const selectedOptions = options.filter((opt) => selected.includes(getValue(opt)));
                  return selectedOptions.map(getLabel).join(', ');
                }
                const selectedOptions = selected as T[];
                return selectedOptions.map(getLabel).join(', ');
              }}
              fullWidth
              size="small"
            >
              {options.map((option, index) => {
                const optionValue = getValue ? getValue(option) : option;
                const key: Key = getValue ? (optionValue as Key) : (index as Key);
                const isChecked = value?.includes(optionValue);

                return (
                  <MenuItem value={optionValue as any} key={key}>
                    <Checkbox size="small" checked={Boolean(isChecked)} />
                    <ListItemText primary={getLabel(option)} />
                  </MenuItem>
                );
              })}
            </Select>
          );
        }}
      />
    </>
  );
};

export default WsCheckmarkSelect;
